/**
 * PostHog Analytics Integration
 *
 * Provides funnel analysis, A/B testing (feature flags), and session replay.
 * Free tier: 1M events/month, 5K sessions, unlimited funnels.
 *
 * Usage:
 *   import * as posthog from './posthog';
 *
 *   posthog.init();
 *   posthog.identify(userId, { email, org_id, ... });
 *   posthog.track('event_name', { param: 'value' });
 *   const variant = posthog.getFeatureFlag('experiment-name');
 */

import posthogLib from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import type { AnalyticsUser } from './types';
import { POSTHOG_API_KEY } from './config';
import { preserveTrackingParams } from './trackingParams';
import { getFirstLandingPage } from './firstTouch';
import { isModernBrowser } from '@/lib/browserCompat';

const IS_PRODUCTION = import.meta.env.PROD;
const DEBUG_MODE =
  typeof window !== 'undefined' && localStorage.getItem('analytics_debug') === 'true';

let initialized = false;

// Browser-extension-injected errors that aren't ours. PostHog's
// capture_exceptions ingests them as $exception events (Sentry already drops
// these via ignoreErrors), polluting the error stream and masking real issues.
const EXTENSION_NOISE_PATTERNS: RegExp[] = [
  /Object Not Found Matching Id/i,
  /Non-Error promise rejection captured with value/i,
];

/** True if a captured $exception is browser-extension noise we should drop. */
export function isExtensionNoiseException(properties?: Record<string, unknown>): boolean {
  if (!properties) return false;
  const parts: string[] = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const ex of list) {
      if (ex && typeof ex === 'object') {
        const value = (ex as Record<string, unknown>).value;
        const type = (ex as Record<string, unknown>).type;
        if (typeof value === 'string') parts.push(value);
        if (typeof type === 'string') parts.push(type);
      }
    }
  }
  if (typeof properties.$exception_message === 'string') parts.push(properties.$exception_message);
  if (typeof properties.$exception_values === 'string') parts.push(properties.$exception_values);
  const message = parts.join(' ');
  return message.length > 0 && EXTENSION_NOISE_PATTERNS.some((re) => re.test(message));
}

/**
 * Initialize PostHog
 * Call once on app load
 */
export function init(): void {
  if (initialized) return;

  // Skip if no API key or not in production (unless debug mode)
  if (!POSTHOG_API_KEY || (!IS_PRODUCTION && !DEBUG_MODE)) {
    return;
  }

  // Skip on legacy/bot browsers below our modern-JS baseline. PostHog lazily
  // loads a web-vitals extension that calls Array.prototype.at(); on ancient
  // engines (e.g. scrapers pinning Chrome 79) that throws an unhandled rejection
  // (Sentry CODEPULSE-BACKEND-C4). Skipping init also keeps bot traffic out of
  // our free-tier event/session-replay quota. Leaving `initialized` false makes
  // identify()/track()/page() no-op for these clients.
  if (!isModernBrowser()) {
    return;
  }

  posthogLib.init(POSTHOG_API_KEY, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: false, // Manual SPA tracking via analytics.page()
    capture_pageleave: true, // Emit $pageleave for true exit pages + time-on-page
    capture_exceptions: true, // PostHog Error Tracking; correlates errors with session replay + funnel (additive to Sentry)
    persistence: 'localStorage',
    autocapture: false, // Use existing custom events for consistency with GA4
    disable_session_recording: false, // Enable session replay
    before_send: (captureResult: CaptureResult | null): CaptureResult | null => {
      if (!captureResult) return captureResult;
      // Drop browser-extension-injected exceptions (not our code).
      if (
        captureResult.event === '$exception' &&
        isExtensionNoiseException(captureResult.properties)
      ) {
        return null;
      }
      return captureResult;
    },
    loaded: () => {
      initialized = true;
    },
  });

  initialized = true;
}

/**
 * Identify user with properties
 * Call after login
 */
export function identify(user: AnalyticsUser): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.identify(user.userId, {
    name: user.name,
    email: user.email,
    github_login: user.githubLogin,
    org_id: user.orgId,
    org_slug: user.orgSlug,
    org_name: user.orgName,
    is_personal: user.isPersonal,
    subscription_tier: user.subscriptionTier,
    is_trial: user.isTrial,
    trial_days_remaining: user.trialDaysRemaining,
    signup_date: user.signupDate,
    first_landing_page: getFirstLandingPage() || undefined,
    logged_in: true,
  });
}

/**
 * Track custom event
 */
export function track(
  eventName: string,
  properties?: Record<string, string | number | boolean | undefined>
): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.capture(eventName, properties);
}

/**
 * Track page view
 */
export function page(path: string, title: string): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.capture('$pageview', {
    // Preserve ad/marketing attribution (utm_*, gclid, …) on the pageview URL so
    // URL-based funnels/breakdowns can attribute traffic. Sensitive params
    // (OAuth code/state, installation_id, token) are stripped by the allowlist.
    $current_url: window.location.origin + path + preserveTrackingParams(window.location.search),
    title,
  });
}

/**
 * Reset user data (on logout)
 */
export function reset(): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.reset();
}

/**
 * Get feature flag value for A/B testing
 *
 * Usage:
 *   const variant = posthog.getFeatureFlag('onboarding-simplified');
 *   if (variant === 'test') {
 *     // Show test variant
 *   }
 *
 * @returns string | boolean | undefined
 */
export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  if (!initialized && !DEBUG_MODE) return undefined;

  return posthogLib.getFeatureFlag(flagKey);
}

/**
 * Check if a feature flag is enabled (boolean flags)
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!initialized && !DEBUG_MODE) return false;

  return posthogLib.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Register super properties (added to all events)
 * Useful for adding context like subscription_tier
 */
export function register(properties: Record<string, unknown>): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.register(properties);
}

/**
 * Manually reload feature flags (useful after user changes)
 */
export function reloadFeatureFlags(): void {
  if (!initialized && !DEBUG_MODE) return;

  posthogLib.reloadFeatureFlags();
}

// Export posthog-js library for direct access (e.g., onFeatureFlags callback)
export { posthogLib };
